import { clsx } from 'clsx'
import * as React from 'react'

import { Heading } from '../Heading'
import { CloseIcon } from '../icons'
import { Paragraph, type ParagraphProps } from '../Paragraph'
import { UnstyledButton } from '../UnstyledButton'
import { VisuallyHidden } from '../VisuallyHidden'

import styles from './PromotionalBanner.module.css'

type PromotionalBannerProps = React.HTMLAttributes<HTMLDivElement> & {
  onDismiss?: React.MouseEventHandler<HTMLButtonElement>
}

type PromotionalBannerContentProps = React.HTMLAttributes<HTMLDivElement>

const PromotionalBannerContent = React.forwardRef<
  HTMLDivElement,
  PromotionalBannerContentProps
>(({ className, ...props }, forwardedRef) => (
  <div
    ref={forwardedRef}
    className={clsx(styles.content, className)}
    {...props}
  />
))

PromotionalBannerContent.displayName = 'PromotionalBanner.Content'

type PromotionalBannerHeadingElement = React.ElementRef<typeof Heading>
type PromotionalBannerHeadingProps = React.ComponentProps<typeof Heading>

const PromotionalBannerHeading = React.forwardRef<
  PromotionalBannerHeadingElement,
  PromotionalBannerHeadingProps
>(({ className, ...props }, forwardedRef) => (
  <Heading
    size="small"
    ref={forwardedRef}
    className={clsx(styles.heading, className)}
    {...props}
  />
))

PromotionalBannerHeading.displayName = 'PromotionalBanner.Heading'

type PromotionalBannerDescriptionProps = ParagraphProps

const PromotionalBannerDescription = React.forwardRef<
  HTMLHeadingElement,
  PromotionalBannerDescriptionProps
>(({ className, ...props }, forwardedRef) => (
  <Paragraph
    ref={forwardedRef}
    className={clsx(styles.description, className)}
    {...props}
  />
))

PromotionalBannerDescription.displayName = 'PromotionalBanner.Description'

type PromotionalBannerActionsProps = React.HTMLAttributes<HTMLDivElement>

const PromotionalBannerActions = React.forwardRef<
  HTMLDivElement,
  PromotionalBannerActionsProps
>(({ className, ...props }, forwardedRef) => (
  <div
    ref={forwardedRef}
    className={clsx(styles.actions, className)}
    {...props}
  />
))

PromotionalBannerActions.displayName = 'PromotionalBanner.Actions'

type PromotionalBannerImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'alt'
> & {
  fallbackColor?: string
}

const PromotionalBannerImage = React.forwardRef<
  HTMLImageElement,
  PromotionalBannerImageProps
>(({ className, fallbackColor = '#e0e0e0', ...props }, forwardedRef) => (
  <div
    className={clsx(styles.image, className)}
    style={{ backgroundColor: fallbackColor }}
  >
    <img loading="lazy" ref={forwardedRef} alt="" {...props} />
  </div>
))

PromotionalBannerImage.displayName = 'PromotionalBanner.Image'

type PromotionalBannerBadgeProps = React.HTMLAttributes<HTMLDivElement>

const PromotionalBannerBadge = React.forwardRef<
  HTMLImageElement,
  PromotionalBannerBadgeProps
>(({ className, ...props }, forwardedRef) => (
  <div
    className={clsx(styles.badge, className)}
    ref={forwardedRef}
    {...props}
  />
))

PromotionalBannerBadge.displayName = 'PromotionalBanner.Badge'

const PromotionalBanner = React.forwardRef<
  HTMLDivElement,
  PromotionalBannerProps
>(
  (
    { className, children, onDismiss, ...props }: PromotionalBannerProps,
    forwardedRef,
  ) => {
    return (
      <div
        ref={forwardedRef}
        role="complementary"
        className={clsx(styles.promotionalBanner, className)}
        {...props}
      >
        {children}
        {onDismiss && (
          <UnstyledButton onClick={onDismiss} className={styles.closeButton}>
            <VisuallyHidden>Dismiss</VisuallyHidden>
            <CloseIcon color="currentColor" />
          </UnstyledButton>
        )}
      </div>
    )
  },
)

PromotionalBanner.displayName = 'PromotionalBanner'

const PromotionalBannerObject = Object.assign(PromotionalBanner, {
  Content: PromotionalBannerContent,
  Heading: PromotionalBannerHeading,
  Description: PromotionalBannerDescription,
  Actions: PromotionalBannerActions,
  Image: PromotionalBannerImage,
  Badge: PromotionalBannerBadge,
})

export {
  PromotionalBannerObject as PromotionalBanner,
  PromotionalBannerActionsProps,
  PromotionalBannerBadgeProps,
  PromotionalBannerContentProps,
  PromotionalBannerDescriptionProps,
  PromotionalBannerHeadingProps,
  PromotionalBannerImageProps,
  PromotionalBannerProps,
}
